import { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Desktop Notifications helper hook.
 *
 * - Reads the current permission state from `Notification.permission`
 * - Exposes `request()` to ask the user (typically wired to a button)
 * - Exposes `notify({ title, body, tag, onClick })` which:
 *     * No-ops if permission isn't 'granted'
 *     * No-ops if the page is currently focused (event already visible)
 *     * Auto-closes after `autoCloseMs` (default 8s)
 *     * Calls `onClick` when the notification is clicked, also focusing
 *       the originating window when possible.
 *
 * Designed to be SSR-safe — checks for `typeof window !== 'undefined'`.
 */
export default function useDesktopNotifications() {
  const supported = typeof window !== 'undefined' && 'Notification' in window;
  const [permission, setPermission] = useState(
    supported ? Notification.permission : 'unsupported',
  );
  const lastNotifRef = useRef(null);

  // Re-read permission whenever the document gains focus (e.g. user changed it
  // in browser settings while the tab was inactive).
  useEffect(() => {
    if (!supported) return undefined;
    const onFocus = () => setPermission(Notification.permission);
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [supported]);

  const request = useCallback(async () => {
    if (!supported) return 'unsupported';
    if (Notification.permission === 'granted') return 'granted';
    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      return result;
    } catch {
      return 'denied';
    }
  }, [supported]);

  const notify = useCallback(
    ({ title, body, tag, icon, onClick, autoCloseMs = 8000, force = false } = {}) => {
      if (!supported) return null;
      if (Notification.permission !== 'granted') return null;
      // Skip if user is actively viewing the tab (avoids double-cueing — they
      // already see the in-app pill / badge). `force=true` overrides this.
      if (!force && typeof document !== 'undefined' && document.visibilityState === 'visible') {
        return null;
      }
      try {
        // Replace any previous notif with the same tag so we don't pile up
        if (lastNotifRef.current && !lastNotifRef.current.closed) {
          try { lastNotifRef.current.close(); } catch { /* noop */ }
        }
        const n = new Notification(title || '', {
          body: body || '',
          tag: tag || 'cb-notif',
          icon,
          renotify: true,
          silent: false,
        });
        lastNotifRef.current = n;
        n.onclick = (ev) => {
          ev?.preventDefault?.();
          try { window.focus(); } catch { /* noop */ }
          onClick?.();
          try { n.close(); } catch { /* noop */ }
        };
        if (autoCloseMs > 0) {
          setTimeout(() => {
            try { n.close(); } catch { /* noop */ }
          }, autoCloseMs);
        }
        return n;
      } catch {
        return null;
      }
    },
    [supported],
  );

  return { supported, permission, request, notify };
}
